/**
 * Edit + actions for a customer record.
 *
 * Editing a customer's own details is reachable from more than one screen
 * (the customer record, and the Customer card on a registration), and both
 * offer it the same way: the platform's edit pen, last in the header after the
 * "..." menu (see EntityCardActions). This is the one control both use, so the
 * affordance cannot drift between them.
 *
 * Edit triggers the tenant's configured ``edit_customer`` action rather than
 * reimplementing the form, so the dialog, its reason gate, composite-field
 * handling, and permission gate all stay in ``EntityActionMenu``.
 */

import type { ReactNode } from 'react'

import { EntityCardActions } from '@/admin/components/shared/entity-card-actions'
import type { EntityAction } from '@/admin/hooks/use-entity-actions'

/** The action that owns editing a customer's own details. */
export const EDIT_CUSTOMER_ACTION_KEY = 'edit_customer'

export function CustomerActionControls({
  consumerUuid,
  holdingUuid,
  onActionComplete,
  onNavigate,
  triggerClassName,
  triggerLabel,
  triggerTestId,
  editButtonTestId = 'customer-edit-btn',
}: {
  consumerUuid: string
  /** The registration the card is on, so an address edit corrects its copy. */
  holdingUuid?: string
  onActionComplete?: () => void
  onNavigate?: (action: EntityAction) => void
  triggerClassName?: string
  triggerLabel?: string
  triggerTestId?: string
  editButtonTestId?: string
}): ReactNode {
  return (
    <EntityCardActions
      entityTypeKey="consumer"
      resourceUuid={consumerUuid}
      holdingUuid={holdingUuid}
      editActionKey={EDIT_CUSTOMER_ACTION_KEY}
      editButtonTestId={editButtonTestId}
      onActionComplete={onActionComplete}
      onNavigate={onNavigate}
      triggerClassName={triggerClassName}
      triggerLabel={triggerLabel}
      triggerTestId={triggerTestId}
    />
  )
}
